import sanitizeHtml from 'sanitize-html';
import { TradingSystem } from '@monorepo/types';
import { contestTermsSanitizeOptions } from '@monorepo/types';
import { decodeHtmlEntities } from './string-utils';

/**
 * Checks if a trading system is a contest trading system
 * @param ts - Trading system to check
 * @returns true if the trading system is MetaTraderContest
 */
export const isContestByTradingSystem = (ts: TradingSystem): boolean =>
  [TradingSystem.MetaTraderContest].includes(ts);

/**
 * Converts a contest name to a URL-friendly hash
 * @param name - Contest name to convert
 * @returns Lowercase name with spaces replaced by hyphens
 */
export const nameToHash = (name: string): string =>
  name.toLocaleLowerCase().replace(/ /g, '-');

/**
 * Converts plain text URLs to clickable anchor tags.
 * Matches URLs starting with http://, https://, or www.
 *
 * @param text - Text containing URLs
 * @returns Text with URLs converted to anchor tags
 */
const convertUrlsToAnchors = (text: string): string => {
  const urlRegex = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;

  return text.replace(urlRegex, (url) => {
    const href = url.startsWith('www.') ? `https://${url}` : url;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
};

/**
 * Normalizes and sanitizes contest terms HTML text.
 * - Decodes HTML entities
 * - Converts plain text URLs to clickable links
 * - Converts escaped newlines to actual newlines
 * - Converts newlines to <br/> tags
 * - Sanitizes HTML with allowed tags
 *
 * @param rawText - Raw terms text from the backend
 * @param options - Sanitization options (defaults to contestTermsSanitizeOptions)
 * @returns Sanitized HTML string ready for dangerouslySetInnerHTML
 */
export const normalizeAndSanitizeTerms = (
  rawText: string,
  options = contestTermsSanitizeOptions,
): string => {
  const decodedText = decodeHtmlEntities(rawText);

  const textWithLinks = convertUrlsToAnchors(decodedText);

  const textWithBreaks = textWithLinks
    .replace(/\\\\n/g, '\n')
    .replace(/\\n/g, '<br/>');

  return sanitizeHtml(textWithBreaks, options);
};
